import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';
import ReportStatCard from './ReportStatCard';
import { projectService } from '../../../services/projectService';
import { parseLocalDate } from '../../../utils/dateUtils';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const PerformanceAnalyticsTab = ({ dateRange }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await projectService?.getAll();
      if (fetchError) throw fetchError;
      setProjects(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const statusCounts = projects?.reduce((acc, p) => {
    const s = p?.status || 'Unknown';
    acc[s] = (acc?.[s] || 0) + 1;
    return acc;
  }, {});

  const statusChartData = Object.entries(statusCounts)?.map(([name, value]) => ({ name, value }));

  const completedProjects = projects?.filter(p => p?.status === 'Completed')?.length;
  const activeProjects = projects?.filter(p => p?.status === 'In Progress' || p?.status === 'Active')?.length;
  const totalProjects = projects?.length;
  const completionRate = totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0;

  const monthlyData = projects?.reduce((acc, p) => {
    if (!p?.startDate) return acc;
    const month = parseLocalDate(p?.startDate)?.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    if (!acc?.[month]) acc[month] = { month, started: 0, completed: 0 };
    acc[month].started += 1;
    if (p?.status === 'Completed') acc[month].completed += 1;
    return acc;
  }, {});

  const monthlyChartData = Object.values(monthlyData)?.slice(-6);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <Icon name="AlertCircle" size={20} className="inline mr-2" />{error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ReportStatCard title="Total Projects" value={totalProjects} icon="Briefcase" color="accent" subtitle="All time" trend={0} trendLabel="" />
        <ReportStatCard title="Active Projects" value={activeProjects} icon="Activity" color="blue" subtitle="Currently running" trend={0} trendLabel="" />
        <ReportStatCard title="Completed" value={completedProjects} icon="CheckCircle2" color="green" subtitle="Successfully finished" trend={0} trendLabel="" />
        <ReportStatCard title="Completion Rate" value={`${completionRate}%`} icon="TrendingUp" color="purple" subtitle="Overall performance" trend={0} trendLabel="" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-foreground mb-4">Projects by Status</h3>
          {statusChartData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={statusChartData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}>
                  {statusChartData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-60 text-muted-foreground text-sm">No data available</div>
          )}
        </div>

        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-foreground mb-4">Monthly Project Activity</h3>
          {monthlyChartData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="started" name="Started" fill="#3B82F6" radius={[4,4,0,0]} />
                <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-60 text-muted-foreground text-sm">No data available</div>
          )}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border">
        <div className="p-5 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">Project List</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Project</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Start Date</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">End Date</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Budget</th>
              </tr>
            </thead>
            <tbody>
              {projects?.slice(0, 10)?.map(p => (
                <tr key={p?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-3">
                    <p className="text-sm font-medium text-foreground">{p?.projectName || p?.name}</p>
                    <p className="text-xs text-muted-foreground">{p?.projectNumber}</p>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      p?.status === 'Completed' ? 'bg-green-100 text-green-700' :
                      p?.status === 'In Progress'? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                    }`}>{p?.status}</span>
                  </td>
                  <td className="px-5 py-3 text-sm text-muted-foreground">{p?.startDate || '—'}</td>
                  <td className="px-5 py-3 text-sm text-muted-foreground">{p?.endDate || '—'}</td>
                  <td className="px-5 py-3 text-sm text-right text-foreground">
                    {p?.totalBudget ? `$${parseFloat(p?.totalBudget)?.toLocaleString()}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {projects?.length === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm">No projects found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalyticsTab;
